import React from "react";

import {
  computeSpaceMemberIds,
  isSpacePrivateInboxEnabled,
} from "@highnote/server/src/core/shared-util";
import { Dialog, DialogSection } from "App/common/Dialog";
import { useSpaceContext } from "App/common/useSpace";
import { EditableArtwork } from "App/components/EditableArtwork";
import { useAuth } from "App/components/Auth";
import { useTrack } from "App/components/useEntities/useTrack";
import { SpaceCTA, SpaceStatus } from "./SpaceCTA";
import { DownloadAppButton } from "App/common/DownloadAppButton";
import styles from "./AppRequiredDialog.module.scss";

const DEFAULT_SPACE_ARTWORK = "/public/default-space-artwork.png";

export const AppRequiredDialog = ({
  open,
  onRequestClose,
}: {
  // Always controlled: a parent opens it imperatively (e.g. tapping the comment
  // input, chat input, or a Home header button on mobile web). The dialog never
  // auto-shows on load — the caller decides when it appears.
  open: boolean;
  onRequestClose: () => void;
}) => {
  const { user } = useAuth();
  const { space, spaceArtworkUrl } = useSpaceContext();
  const { trackArtworkUrl } = useTrack();

  const memberIds = computeSpaceMemberIds(space?.rolesV2, space?.createdBy);

  // The facepile normally shows the space's members. When there are none to
  // show (e.g. opened from Home, which has no space), fall back to the current
  // user's own avatar so the lockup never reads as empty.
  const members = memberIds.length
    ? memberIds.map((id) => ({ id }))
    : user
      ? [{ id: user.id }]
      : [];

  // A private-inbox space never shows this prompt.
  if (space && isSpacePrivateInboxEnabled(space)) {
    return null;
  }

  return (
    <Dialog
      className={styles.appRequired__container}
      open={open}
      onClose={onRequestClose}
      hideCloseButton
    >
      <DialogSection className={styles.appRequired__artwork}>
        <div className={styles.appRequired__artworkFrame}>
          <EditableArtwork
            disabled
            // Track's own art first, inheriting the space art when the track has
            // none — mirrors how entity rows resolve artwork on the space page
            // (`entity.artworkFile || parentArtworkFile` in EntityPreview).
            // `trackArtworkUrl` is only the track's own art (no space fallback
            // baked in), so the fallback happens here.
            artworkUrl={trackArtworkUrl || spaceArtworkUrl || ""}
            fallbackArtworkUrl={DEFAULT_SPACE_ARTWORK}
            blurredBackground="large"
          />
        </div>
      </DialogSection>
      <DialogSection>
        {/* Static lockup: commenting requires the app, so the status is fixed
            "APP REQUIRED" and the CTA is the app download. No countdown or
            membership logic here — uses the presentational SpaceCTA directly. */}
        <SpaceCTA
          status={<SpaceStatus label="APP REQUIRED" dot="red" />}
          members={members}
          memberCount={members.length}
          cta={<DownloadAppButton fullWidth />}
        />
      </DialogSection>
    </Dialog>
  );
};
